/**
 * Base abstract class for authentication providers
 * Each provider implements a specific authentication method
 */
export default class BaseAuthProvider {
    constructor(service) {
        if (new.target === BaseAuthProvider) {
            throw new TypeError("Cannot construct BaseAuthProvider instances directly")
        }
        this.service = service
    }

    /**
     * Authenticate user with provider-specific credentials
     * @param {Object} credentials - Provider-specific credentials
     * @returns {Promise<Object>} Authentication result: {token, type, jwt?, refresh_token?, ...}
     */
    async authenticate(credentials) {
        throw new Error("Method 'authenticate()' must be implemented")
    }

    /**
     * Logout and cleanup provider-specific data
     * @param {Object} tokenData - Current token data from store
     * @returns {Promise<void>}
     */
    async logout(tokenData) {
        // Default implementation: no-op
        // Providers can override for custom cleanup (e.g., LDAP revoke token)
    }

    /**
     * Refresh authentication token (if supported by provider)
     * @param {Object} tokenData - Current token data including refresh_token
     * @returns {Promise<Object>} New authentication result: {token, type, jwt?, refresh_token?, ...}
     */
    async refreshToken(tokenData) {
        // Default implementation: not supported
        throw new Error("Token refresh not supported by this provider")
    }

    /**
     * Check if provider supports token refresh
     * @returns {boolean}
     */
    supportsRefresh() {
        return false
    }

    /**
     * Get provider method identifier
     * @returns {string} Method identifier (e.g., 'password', 'ldap', 'email_token')
     */
    getMethod() {
        throw new Error("Method 'getMethod()' must be implemented")
    }

    /**
     * Get alternative providers that can be used instead of this one
     * Each provider defines its own alternatives based on business logic
     * @param {Function} isEnabled - Function to check if a provider is enabled
     * @returns {Array<string>} Array of alternative provider method names
     */
    getAlternatives(isEnabled) {
        // Default: no alternatives
        return []
    }
}
